/**
 * What happens at the airport before a flight, and when. Airlines publish cutoffs relative to
 * the scheduled departure; boarding follows the departure time actually expected. Real times
 * (a boarding time from the boarding pass) win over these rules of thumb.
 */

export type MilestoneKey = 'checkin' | 'bagDrop' | 'boarding' | 'doorsClose';

export interface FlightMilestone {
  key: MilestoneKey;
  label: string;
  at: number;
  /** True when it's a rule of thumb rather than a time the airline gave. */
  estimated: boolean;
}

interface Rules {
  /** Online check-in opens, hours before departure. */
  checkinHours: number;
  /** Bags must be checked, minutes before scheduled departure. */
  bagDrop: number;
  /** Boarding begins, minutes before departure. */
  boarding: number;
  /** Boarding door closes, minutes before departure. */
  doorsClose: number;
}

const DOMESTIC: Rules = { checkinHours: 24, bagDrop: 45, boarding: 40, doorsClose: 15 };
const INTERNATIONAL: Rules = { checkinHours: 24, bagDrop: 60, boarding: 50, doorsClose: 15 };

/** Where a carrier's habits differ from the usual. */
const AIRLINE_RULES: Record<string, { domestic?: Partial<Rules>; international?: Partial<Rules> }> =
  {
    WN: { domestic: { boarding: 30, doorsClose: 10 } },
  };

const MIN = 60_000;

export function flightMilestones(opts: {
  /** Scheduled departure (gate out). */
  scheduledDepart: number;
  /** Expected departure, when it has moved. */
  expectedDepart?: number | null;
  airline?: string | null;
  international: boolean;
  boardingAt?: number | null;
}): FlightMilestone[] {
  const kind = opts.international ? 'international' : 'domestic';
  const rules: Rules = {
    ...(opts.international ? INTERNATIONAL : DOMESTIC),
    ...AIRLINE_RULES[opts.airline?.toUpperCase() ?? '']?.[kind],
  };
  const sched = opts.scheduledDepart;
  const expected = opts.expectedDepart ?? sched;
  // A known boarding time moves with a delay by the same amount.
  const boarding =
    opts.boardingAt != null
      ? opts.boardingAt + (expected - sched)
      : expected - rules.boarding * MIN;
  return [
    {
      key: 'checkin',
      label: 'Check-in opens',
      at: sched - rules.checkinHours * 60 * MIN,
      estimated: true,
    },
    { key: 'bagDrop', label: 'Bag drop closes', at: sched - rules.bagDrop * MIN, estimated: true },
    { key: 'boarding', label: 'Boarding', at: boarding, estimated: opts.boardingAt == null },
    {
      key: 'doorsClose',
      label: 'Doors close',
      at: expected - rules.doorsClose * MIN,
      estimated: true,
    },
  ];
}

/** The next milestone still ahead, if any. */
export function nextMilestone(list: FlightMilestone[], now: number): FlightMilestone | null {
  return list.find((m) => m.at > now) ?? null;
}
